import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ExpenseServiceWrapper } from '../../services/expenseWrapper';
import { CognitoAdminService } from '../../services/cognitoAdmin';
import { Expense, ExpenseStatus } from '../../types/expense';
import { User } from '../../types/user';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './MonthlySalarySummary.css';

interface EmployeeReimbursement {
  employeeId: string;
  employeeName: string;
  employeeEmail: string;
  department: string;
  totalAmount: number;
  expenseCount: number;
  expenses: Expense[];
}

interface MonthlySummaryData {
  month: string; // YYYY-MM
  monthLabel: string; // "January 2026"
  totalAmount: number;
  totalExpenses: number;
  employeeCount: number;
  employees: EmployeeReimbursement[];
}

/**
 * Monthly Salary Reimbursement Summary Component
 * For Finance Admin to view and export monthly reimbursements for payroll
 */
const MonthlySalarySummary: React.FC = () => {
  const { user } = useAuth();
  const [allExpenses, setAllExpenses] = useState<Expense[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [selectedStatus, setSelectedStatus] = useState<ExpenseStatus | 'ALL'>(ExpenseStatus.REIMBURSED);
  const [summaryData, setSummaryData] = useState<MonthlySummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedEmployee, setExpandedEmployee] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Status options for filter
  const statusOptions: { value: ExpenseStatus | 'ALL'; label: string; color: string }[] = [
    { value: 'ALL', label: 'All Statuses', color: '#6b7280' },
    { value: ExpenseStatus.REIMBURSED, label: 'Reimbursed', color: '#10b981' },
    { value: ExpenseStatus.FINANCE_APPROVED, label: 'Finance Approved', color: '#3b82f6' },
    { value: ExpenseStatus.MANAGER_APPROVED, label: 'Manager Approved', color: '#8b5cf6' },
    { value: ExpenseStatus.SUBMITTED, label: 'Submitted', color: '#f59e0b' },
    { value: ExpenseStatus.DRAFT, label: 'Draft', color: '#9ca3af' },
    { value: ExpenseStatus.MANAGER_REJECTED, label: 'Manager Rejected', color: '#ef4444' },
    { value: ExpenseStatus.FINANCE_REJECTED, label: 'Finance Rejected', color: '#dc2626' },
  ];

  // Generate month options (last 12 months + "All Months")
  const getMonthOptions = (): { value: string; label: string }[] => {
    const options: { value: string; label: string }[] = [
      { value: 'ALL', label: 'All Months' }
    ];
    const now = new Date();

    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }

    return options;
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (allExpenses.length > 0) {
      calculateSummary();
    }
  }, [selectedMonth, selectedStatus, allExpenses, allUsers]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load users
      let users: User[] = [];
      try {
        const adminEndpoint = process.env.REACT_APP_ADMIN_API_ENDPOINT;
        if (adminEndpoint) {
          const response = await CognitoAdminService.listUsers();
          if (response.users) {
            users = response.users.map((u: any) => ({
              id: u.username || u.email || '',
              // Azure-federated accounts have no email attribute in Cognito.
              email: u.email || '',
              name: u.name || 'Unknown',
              employeeId: u.employeeId || '',
              department: u.department || 'Unknown',
              managerEmail: u.managerEmail,
              role: CognitoAdminService.mapCognitoRole(u.role),
              createdAt: u.createdAt || new Date().toISOString(),
              updatedAt: u.updatedAt || new Date().toISOString()
            }));
          }
        }
      } catch (userError) {
        console.log('[Salary Summary] Could not load users:', userError);
      }
      setAllUsers(users);

      // Load all expenses
      const expensesResult = await ExpenseServiceWrapper.getAllExpenses();
      setAllExpenses(expensesResult.items || []);
    } catch (err) {
      console.error('[Salary Summary] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };


  // Both sides of this lookup can legitimately be missing:
  //   - Azure-federated Cognito accounts carry no `email` attribute at all, so
  //     `allUsers` contains entries whose email is undefined.
  //   - A stored expense row can be missing `submitterId`.
  // Calling .toLowerCase() on either used to throw and take the whole page down.
  const getUserInfo = (submitterId?: string | null): { name: string; email: string; department: string; employeeId: string } => {
    const id = (submitterId || '').trim();
    const idLower = id.toLowerCase();

    const foundUser = id
      ? allUsers.find(u =>
          u.id === id ||
          u.email === id ||
          (u.email || '').toLowerCase() === idLower
        )
      : undefined;

    if (foundUser) {
      return {
        name: foundUser.name || 'Unknown',
        email: foundUser.email || id,
        department: foundUser.department || 'Unknown',
        employeeId: foundUser.employeeId || ''
      };
    }

    // Fallback
    if (id.includes('@')) {
      const namePart = id.split('@')[0];
      return {
        name: namePart.split(/[._-]/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
        email: id,
        department: 'Unknown',
        employeeId: ''
      };
    }

    return { name: 'Unknown', email: id || 'unknown', department: 'Unknown', employeeId: '' };
  };

  const calculateSummary = () => {
    console.log('[Salary Summary] ===== CALCULATION START =====');
    console.log('[Salary Summary] Selected Month:', selectedMonth);
    console.log('[Salary Summary] Selected Status:', selectedStatus);
    console.log('[Salary Summary] Total expenses loaded:', allExpenses.length);

    // Log all unique statuses in the loaded expenses
    const uniqueStatuses = [...new Set(allExpenses.map(e => e.status))];
    console.log('[Salary Summary] Unique statuses in database:', uniqueStatuses);

    // Filter expenses for selected month and status
    const monthExpenses = allExpenses.filter(expense => {
      // Month matching: use updatedAt (when status changed / reimbursed) for payroll relevance
      // Fall back to expense.date if updatedAt is not available
      let monthMatch = true;
      if (selectedMonth !== 'ALL') {
        // Use updatedAt for status-change date (most relevant for payroll)
        // Also check expense.date so expenses show up in either month
        const updatedDate = new Date(expense.updatedAt || expense.date);
        const updatedMonth = `${updatedDate.getFullYear()}-${String(updatedDate.getMonth() + 1).padStart(2, '0')}`;

        const expenseDate = new Date(expense.date);
        const expenseDateMonth = `${expenseDate.getFullYear()}-${String(expenseDate.getMonth() + 1).padStart(2, '0')}`;

        // Match if either the expense date or the last update date falls in the selected month
        monthMatch = updatedMonth === selectedMonth || expenseDateMonth === selectedMonth;
      }

      // Very robust status comparison - handles any case and spacing variations
      let statusMatch = false;
      if (selectedStatus === 'ALL') {
        statusMatch = true;
      } else {
        const expenseStatusNormalized = (expense.status || '').toLowerCase().trim().replace(/\s+/g, ' ');
        const selectedStatusNormalized = selectedStatus.toLowerCase().trim().replace(/\s+/g, ' ');
        statusMatch = expenseStatusNormalized === selectedStatusNormalized;
      }

      if (statusMatch && selectedStatus !== 'ALL') {
        console.log('[Salary Summary] Checking expense:', {
          id: expense.id?.substring(0, 8),
          title: expense.title,
          status: expense.status,
          date: expense.date,
          updatedAt: expense.updatedAt,
          monthMatch,
          statusMatch
        });
      }

      return monthMatch && statusMatch;
    });

    console.log('[Salary Summary] Filtered expenses count:', monthExpenses.length);
    console.log('[Salary Summary] ===== CALCULATION END =====');

    // Group by employee
    const employeeMap = new Map<string, EmployeeReimbursement>();

    monthExpenses.forEach(expense => {
      const userInfo = getUserInfo(expense.submitterId);
      const key = userInfo.email.toLowerCase();

      // A row stored without an amount must not turn every total into NaN.
      const amount = Number.isFinite(expense.amount) ? expense.amount : 0;

      if (employeeMap.has(key)) {
        const existing = employeeMap.get(key)!;
        existing.totalAmount += amount;
        existing.expenseCount += 1;
        existing.expenses.push(expense);
      } else {
        employeeMap.set(key, {
          employeeId: userInfo.employeeId,
          employeeName: userInfo.name,
          employeeEmail: userInfo.email,
          department: userInfo.department,
          totalAmount: amount,
          expenseCount: 1,
          expenses: [expense]
        });
      }
    });

    // Sort employees by name
    const employees = Array.from(employeeMap.values()).sort((a, b) =>
      (a.employeeName || '').localeCompare(b.employeeName || '')
    );

    const monthLabel = selectedMonth === 'ALL'
      ? 'All Months'
      : new Date(selectedMonth + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    setSummaryData({
      month: selectedMonth,
      monthLabel,
      totalAmount: employees.reduce((sum, emp) => sum + emp.totalAmount, 0),
      totalExpenses: monthExpenses.length,
      employeeCount: employees.length,
      employees
    });
  };

  const formatAmount = (amount: number): string => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(Number.isFinite(amount) ? amount : 0);
  };

  // Format amount for CSV export (uses INR text instead of ₹ symbol for compatibility)
  const formatAmountForExport = (amount: number): string => {
    return `INR ${new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)}`;
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return '-';
    const parsed = new Date(dateStr);
    if (Number.isNaN(parsed.getTime())) return '-';
    return parsed.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const toggleEmployeeExpand = (email: string) => {
    setExpandedEmployee(expandedEmployee === email ? null : email);
  };

  const getStatusLabel = (): string => {
    if (selectedStatus === 'ALL') return 'All Statuses';
    return selectedStatus;
  };

  const exportToCSV = () => {
    if (!summaryData) return;
    setExporting(true);

    try {
      const lines: string[] = [];

      // Header section
      lines.push(`"Monthly Expense Summary"`);
      lines.push(`"Month","${summaryData.monthLabel}"`);
      lines.push(`"Status Filter","${getStatusLabel()}"`);
      lines.push(`"Generated On","${new Date().toLocaleString('en-IN')}"`);
      lines.push(`"Generated By","${user?.name || 'Finance Admin'}"`);
      lines.push('');

      // Summary section
      lines.push(`"Summary"`);
      lines.push(`"Total Employees","${summaryData.employeeCount}"`);
      lines.push(`"Total Expenses","${summaryData.totalExpenses}"`);
      lines.push(`"Total Amount","${formatAmountForExport(summaryData.totalAmount)}"`);
      lines.push('');

      // Employee-wise summary for payroll
      lines.push(`"Employee-wise Summary"`);
      lines.push(`"Employee ID","Employee Name","Email","Department","No. of Expenses","Total Amount (INR)"`);

      summaryData.employees.forEach(emp => {
        lines.push(`"${emp.employeeId}","${emp.employeeName}","${emp.employeeEmail}","${emp.department}","${emp.expenseCount}","${emp.totalAmount.toFixed(2)}"`);
      });

      lines.push('');
      lines.push(`"Grand Total","","","","${summaryData.totalExpenses}","${summaryData.totalAmount.toFixed(2)}"`);
      lines.push('');

      // Detailed breakdown
      lines.push(`"Detailed Expense Breakdown"`);
      lines.push(`"Employee Name","Expense Title","Category","Amount (INR)","Date","Status","Payment Method"`);

      summaryData.employees.forEach(emp => {
        emp.expenses.forEach(expense => {
          lines.push(`"${emp.employeeName}","${expense.title}","${expense.category}","${expense.amount.toFixed(2)}","${formatDate(expense.date)}","${expense.status}","${expense.paymentMethod}"`);
        });
      });

      const csvContent = lines.join('\n');
      // Add BOM for proper UTF-8 encoding (fixes ₹ symbol in Excel)
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const statusSuffix = selectedStatus === 'ALL' ? 'all' : selectedStatus.toLowerCase().replace(/\s+/g, '-');
      const fileName = `expense-summary-${summaryData.month}-${statusSuffix}.csv`;
      saveAs(blob, fileName);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };


  const exportToPDF = async () => {
    if (!summaryData) return;
    setExporting(true);

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      let yPos = 20;

      // Title
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('Monthly Expense Summary', pageWidth / 2, yPos, { align: 'center' });
      yPos += 12;

      // Subtitle
      doc.setFontSize(14);
      doc.setFont('helvetica', 'normal');
      doc.text(summaryData.monthLabel, pageWidth / 2, yPos, { align: 'center' });
      yPos += 12;

      // Metadata box
      doc.setFillColor(245, 247, 250);
      doc.rect(15, yPos, pageWidth - 30, 20, 'F');
      doc.setFontSize(9);
      doc.setTextColor(60, 60, 60);
      doc.text(`Status: ${getStatusLabel()}`, 20, yPos + 7);
      doc.text(`Generated: ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} ${new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`, 20, yPos + 13);
      doc.text(`Generated By: ${user?.name || 'Chidvilaas Sridhanta'}`, 20, yPos + 19);
      doc.setTextColor(0, 0, 0);
      yPos += 28;

      // Summary Box with better styling
      doc.setFillColor(59, 130, 246);
      doc.rect(15, yPos, pageWidth - 30, 8, 'F');
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text('Summary', 20, yPos + 6);
      doc.setTextColor(0, 0, 0);
      yPos += 8;

      doc.setFillColor(240, 245, 255);
      doc.rect(15, yPos, pageWidth - 30, 18, 'F');
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Total Employees: ${summaryData.employeeCount}`, 20, yPos + 7);
      doc.text(`Total Expenses: ${summaryData.totalExpenses}`, 80, yPos + 7);
      doc.text(`Total Amount: ${formatAmount(summaryData.totalAmount)}`, 20, yPos + 14);
      yPos += 26;

      // Employee-wise table for payroll
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Employee-wise Reimbursement (For Payroll)', 15, yPos);
      yPos += 8;

      const tableData = summaryData.employees.map(emp => [
        emp.employeeId || '-',
        emp.employeeName,
        emp.department,
        emp.expenseCount.toString(),
        formatAmount(emp.totalAmount)
      ]);

      // Add grand total row
      tableData.push([
        '',
        'GRAND TOTAL',
        '',
        summaryData.totalExpenses.toString(),
        formatAmount(summaryData.totalAmount)
      ]);

      autoTable(doc, {
        startY: yPos,
        head: [['Emp ID', 'Employee Name', 'Department', 'Expenses', 'Amount']],
        body: tableData,
        theme: 'striped',
        headStyles: {
          fillColor: [59, 130, 246],
          fontSize: 10,
          fontStyle: 'bold',
          halign: 'left'
        },
        bodyStyles: {
          fontSize: 9,
          cellPadding: 4
        },
        columnStyles: {
          0: { cellWidth: 25, halign: 'left' },
          1: { cellWidth: 55, halign: 'left' },
          2: { cellWidth: 40, halign: 'left' },
          3: { cellWidth: 25, halign: 'center' },
          4: { cellWidth: 35, halign: 'right', fontStyle: 'normal' }
        },
        margin: { left: 15, right: 15 },
        didParseCell: (data) => {
          // Style the grand total row
          if (data.row.index === tableData.length - 1 && data.section === 'body') {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = [220, 230, 240];
            data.cell.styles.textColor = [0, 0, 0];
            data.cell.styles.fontSize = 10;
          }
        }
      });

      yPos = (doc as any).lastAutoTable.finalY + 15;

      // Add detailed breakdown on new page
      doc.addPage();
      yPos = 20;

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Detailed Expense Breakdown', 15, yPos);
      yPos += 8;

      const detailData: string[][] = [];
      summaryData.employees.forEach(emp => {
        emp.expenses.forEach(expense => {
          detailData.push([
            emp.employeeName.length > 20 ? emp.employeeName.substring(0, 20) + '...' : emp.employeeName,
            expense.title.length > 30 ? expense.title.substring(0, 30) + '...' : expense.title,
            expense.category,
            formatDate(expense.date),
            formatAmount(expense.amount)
          ]);
        });
      });

      autoTable(doc, {
        startY: yPos,
        head: [['Employee', 'Expense Title', 'Category', 'Date', 'Amount']],
        body: detailData,
        theme: 'striped',
        headStyles: {
          fillColor: [59, 130, 246],
          fontSize: 9,
          fontStyle: 'bold'
        },
        bodyStyles: {
          fontSize: 8,
          cellPadding: 3
        },
        columnStyles: {
          0: { cellWidth: 35 },
          1: { cellWidth: 55 },
          2: { cellWidth: 30 },
          3: { cellWidth: 25 },
          4: { cellWidth: 30, halign: 'right' }
        },
        margin: { left: 15, right: 15 }
      });

      // Footer on all pages
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(
          `Page ${i} of ${pageCount}`,
          pageWidth / 2,
          doc.internal.pageSize.height - 10,
          { align: 'center' }
        );
      }

      const statusSuffix = selectedStatus === 'ALL' ? 'all' : selectedStatus.toLowerCase().replace(/\s+/g, '-');
      const fileName = `expense-summary-${summaryData.month}-${statusSuffix}.pdf`;
      doc.save(fileName);
    } catch (err) {
      console.error('PDF export failed:', err);
      alert('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="salary-summary">
        <div className="loading">Loading salary summary...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="salary-summary">
        <div className="error">
          <h3>Error Loading Data</h3>
          <p>{error}</p>
          <button onClick={loadData} className="retry-button">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="salary-summary">
      <div className="summary-header">
        <div className="header-left">
          <h2>Monthly Expense Summary</h2>
          <p>View and export employee expenses by month and status</p>
        </div>
        <div className="header-right">
          <div className="filter-group">
            <label>Month:</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="month-selector"
            >
              {getMonthOptions().map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Status:</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value as ExpenseStatus | 'ALL')}
              className="status-selector"
            >
              {statusOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Status Badge */}
      <div className="status-badge-container">
        <span className={`status-badge status-${selectedStatus === 'ALL' ? 'all' : selectedStatus.toLowerCase().replace(/\s+/g, '-')}`}>
          Showing: {getStatusLabel()} expenses for {summaryData?.monthLabel || selectedMonth}
        </span>
      </div>

      {summaryData && (
        <>
          {/* Summary Cards */}
          <div className="summary-cards">
            <div className="summary-card">
              <div className="card-icon">👥</div>
              <div className="card-content">
                <span className="card-value">{summaryData.employeeCount}</span>
                <span className="card-label">Employees</span>
              </div>
            </div>
            <div className="summary-card">
              <div className="card-icon">📄</div>
              <div className="card-content">
                <span className="card-value">{summaryData.totalExpenses}</span>
                <span className="card-label">Expenses</span>
              </div>
            </div>
            <div className="summary-card highlight">
              <div className="card-icon">💰</div>
              <div className="card-content">
                <span className="card-value">{formatAmount(summaryData.totalAmount)}</span>
                <span className="card-label">Total Reimbursement</span>
              </div>
            </div>
          </div>

          {/* Export Buttons */}
          <div className="export-section">
            <span className="export-label">Export for Payroll:</span>
            <button
              onClick={exportToCSV}
              disabled={exporting || summaryData.employees.length === 0}
              className="export-btn csv"
            >
              {exporting ? 'Exporting...' : '📊 Export CSV'}
            </button>
            <button
              onClick={exportToPDF}
              disabled={exporting || summaryData.employees.length === 0}
              className="export-btn pdf"
            >
              {exporting ? 'Exporting...' : '📑 Export PDF'}
            </button>
          </div>

          {/* Employee Table */}
          {summaryData.employees.length === 0 ? (
            <div className="no-data">
              <p>No expenses found for {summaryData.monthLabel}</p>
              <p className="hint">
                {selectedStatus === 'ALL'
                  ? 'No expenses exist for this month.'
                  : `No expenses with "${selectedStatus}" status found. Try selecting a different status.`}
              </p>
            </div>
          ) : (
            <div className="employee-table">
              <table>
                <thead>
                  <tr>
                    <th>Employee ID</th>
                    <th>Employee Name</th>
                    <th>Department</th>
                    <th>Expenses</th>
                    <th>Total Amount</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryData.employees.map(emp => (
                    <React.Fragment key={emp.employeeEmail}>
                      <tr className="employee-row">
                        <td>{emp.employeeId || '-'}</td>
                        <td>
                          <div className="employee-info">
                            <span className="name">{emp.employeeName}</span>
                            <span className="email">{emp.employeeEmail}</span>
                          </div>
                        </td>
                        <td>{emp.department}</td>
                        <td className="center">{emp.expenseCount}</td>
                        <td className="amount">{formatAmount(emp.totalAmount)}</td>
                        <td>
                          <button
                            className="expand-btn"
                            onClick={() => toggleEmployeeExpand(emp.employeeEmail)}
                          >
                            {expandedEmployee === emp.employeeEmail ? '▼ Hide' : '▶ View'}
                          </button>
                        </td>
                      </tr>
                      {expandedEmployee === emp.employeeEmail && (
                        <tr className="expense-details-row">
                          <td colSpan={6}>
                            <div className="expense-details">
                              <table className="inner-table">
                                <thead>
                                  <tr>
                                    <th>Title</th>
                                    <th>Category</th>
                                    <th>Date</th>
                                    <th>Status</th>
                                    <th>Payment Method</th>
                                    <th>Amount</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {emp.expenses.map(expense => (
                                    <tr key={expense.id}>
                                      <td>{expense.title}</td>
                                      <td>{expense.category}</td>
                                      <td>{formatDate(expense.date)}</td>
                                      <td><span className={`expense-status status-${(expense.status || '').toLowerCase().replace(/\s+/g, '-')}`}>{expense.status || '-'}</span></td>
                                      <td>{expense.paymentMethod}</td>
                                      <td className="amount">{formatAmount(expense.amount)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="total-row">
                    <td colSpan={3}><strong>Grand Total</strong></td>
                    <td className="center"><strong>{summaryData.totalExpenses}</strong></td>
                    <td className="amount"><strong>{formatAmount(summaryData.totalAmount)}</strong></td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MonthlySalarySummary;
